import { type DjangoWaffleType } from "@shared/contexts/WaffleContext/django-waffle-type";
import { checkFlagActive } from "@shared/contexts/WaffleContext/WaffleContext";
import { extractVenueId } from "@shared/modules/venue/helpers/helpers";
import { isTrafficSplitEnabled } from "./isTrafficSplitEnabled";
import { URL_MAP, URLMapEntry } from "./constants";

// These values will be replaced at build time with the actual environment values
declare const process: {
  env: {
    SITE_URL: string;
    APP_ENV: string;
  };
};

export class ConfigService {
  host: string;
  environment: string;
  waffleReady: Promise<boolean> | undefined;

  constructor() {
    this.host = process.env.SITE_URL.replace(/\/$/, "");
    this.environment = process.env.APP_ENV;

    if (!window.waffle) {
      this.loadWaffle();
    }
  }

  private loadWaffle = () => {
    this.waffleReady = new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = `${this.host}/wafflejs`;
      script.crossOrigin = "use-credentials";
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.head.appendChild(script);
    });
  };

  // This function will return the full URL for the given entry taking into
  // account if the FF to use the NextJS widget is enabled or not.
  private getUrl = async (entry: URLMapEntry, ...params: unknown[]) => {
    await this.waffleReady;

    if (!("flag" in entry)) {
      return `${this.host}${entry.nextjsPath(...params)}`;
    }

    const { oldPath, nextjsPath, flag: flagName } = entry;

    if (flagName && !this.flag(flagName)) {
      return `${this.host}${oldPath(...params)}`;
    }

    const path = nextjsPath
      ? nextjsPath(...params)
      : oldPath(...params).replace(/^\/w\//, "/widget/");

    return `${this.host}${path}`;
  };

  flag = (flagName: keyof DjangoWaffleType["FLAGS"]) =>
    window.waffle?.FLAGS?.[flagName];

  loginWidgetUrl = () => this.getUrl(URL_MAP.login);

  checkoutWidgetUrl = () => this.getUrl(URL_MAP.checkout);

  productPurchaseWidgetUrl = (id: string) =>
    this.getUrl(URL_MAP.productPurchase, id);

  expressCheckoutWidgetUrl = () => this.getUrl(URL_MAP.expressCheckout);

  eventPurchaseWidgetUrl = (slug: string) =>
    this.getUrl(URL_MAP.eventPurchase, slug);

  calendarWidgetUrl = async (
    venueIdOrSlug: string | number,
    isAttraction = false,
    eventId?: string
  ) => {
    await this.waffleReady;

    const venueSlug = venueIdOrSlug.toString();

    // Extract venue ID for attraction flag check only
    const venueId = await extractVenueId(venueIdOrSlug, this.host);
    const waffle = window.waffle as DjangoWaffleType;
    const isV2FlagEnabled =
      venueId !== null &&
      checkFlagActive(waffle, "sdk_use_v2_attraction_calendar_widget", venueId);
    const shouldUseTrafficSplit =
      isV2FlagEnabled &&
      (await isTrafficSplitEnabled(
        "new_attraction_calendar",
        venueId,
        this.host
      ));

    // Check if we should use v2 attraction calendar widget
    // Only for attractions with valid venueId and feature flag enabled
    const shouldUseNewAttractionCalendar =
      isAttraction && shouldUseTrafficSplit;

    if (shouldUseNewAttractionCalendar) {
      return `${this.host}/widget/calendar/attraction/${eventId}`;
    }

    // Default calendar widget URL (standard flow)
    return this.getUrl(URL_MAP.calendar, venueSlug, isAttraction);
  };

  basketWidgetUrl = () => this.getUrl(URL_MAP.basket);

  membershipPurchaseWidgetUrl = (id: string) =>
    this.getUrl(URL_MAP.membership, id);
}
